import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ACTION_SUBMIT_PROCESS_OUTPUTS} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import makeSimpleActionFunction, {
  getSubmitActionFunctionIcon,
  getSubmitButtonHidden,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  ActionResultType,
  batchedSubmissionGenerator,
  getSubmissionBatchSize,
  performServerSideTableWriteActionWithAudits,
  ServerSideWriteActionResult,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import {
  StructuredTableKind,
  TableActionFunctionParameters,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableSubmissionDiffs} from 'notebook/structured-tables/util/datamods/getStructuredTableCellDiffOnSubmission';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import {getSourceTypeForStructuredTable} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import BatchSubmitOutputsTaskUpdateManager from 'notebook/util/BatchSubmitOutputsTaskUpdateManager';
import notifications from 'notifications';
import {makeClientActionId} from 'processes/dao/ProcessManagementDAO';
import {CreateOrUpdateServerProcessOutput} from 'processes/dao/ProcessManagementDAO';
import {outputTableToOutputsForCreateOrUpdate} from 'processes/tables/outputTableToOutputsUtils';
import {
  ProcessOutputStructuredTable,
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata,
} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import submitOutputsWithConfirmation from 'processes/tables/submitOutputsWithConfirmation';
import {ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

import getDataModsCountForTableSubmission from '../../../processes/tables/getDataModsCountForTableSubmission';
import {getRowAwareLoadingMessage, RowSubmissionRange} from './makeSubmitRowActionFunction';

interface UpdateProcessTaskStatusParam {
  id: string;
  statusId: string;
}

export interface ProcessOutputSubmitParams {
  processOutputs: Array<CreateOrUpdateServerProcessOutput>;
  processTasks?: Array<UpdateProcessTaskStatusParam>;
}

async function attemptBatchedCreateOutputs(
  entryId: string,
  structuredTable: ProcessOutputStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  outputSubmitParams: ProcessOutputSubmitParams,
  tableSubmissionDiffs?: TableSubmissionDiffs,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.ProcessOutputSubmitResponse>> {
  const responseData: ActionFunctionDAO.ProcessOutputSubmitResponse = {outputIds: [], entryVersionId: -1};
  let latestVersionId: number | undefined;
  let partialSuccess = false;
  let submittedRange: RowSubmissionRange = {
    start: selection ? selection.start : 0,
    end: selection ? selection.start : 0,
  };
  const actionId = makeClientActionId();

  const taskUpdateManager = new BatchSubmitOutputsTaskUpdateManager({
    ...outputSubmitParams,
    willProcessBatchesInOrder: true,
  });
  const batchSize = getSubmissionBatchSize(StructuredTableKind.PROCESS_OUTPUT);
  const batches = batchedSubmissionGenerator(
    computedTable,
    batchSize,
    structuredTable,
    selection,
    tableSubmissionDiffs
  );
  let batch = batches.next();
  try {
    while (!batch.done) {
      const {range, evaluatedRowBatch, tableSubmissionDiffBatch} = batch.value;
      // generate the next batch so we can determine if we are on the last iteration
      batch = batches.next();
      if (batchSize && updateStatus) {
        updateStatus(
          getRowAwareLoadingMessage(range, 'Updating Results For Rows', 'Updating Results For Row')
        );
      }
      const batchInfo = {actionId, lastBatch: batch.done === true};
      const batchStartIdx = selection?.start ? range.start - selection.start : range.start;
      const batchEndIdxPlusOne = selection?.start ? range.end - selection.start : range.end;

      const outputs = outputSubmitParams.processOutputs.slice(batchStartIdx, batchEndIdxPlusOne);
      const tasks = taskUpdateManager.getTaskUpdatesForBatch({start: batchStartIdx, end: batchEndIdxPlusOne});

      const outputSubmitParamsBatch = {
        processOutputs: outputs,
        ...(tasks.length > 0 && {processTasks: tasks}),
      };

      const batchResult = await ActionFunctionDAO.submitProcessOutputs(
        entryId,
        structuredTable,
        evaluatedRowBatch,
        actionFunctionContext,
        outputSubmitParamsBatch,
        batchInfo,
        tableSubmissionDiffBatch,
        range,
        latestVersionId,
        dataModsHeader
      );

      latestVersionId = batchResult.entryVersionId;
      submittedRange = {...submittedRange, end: range.end};
      responseData.outputIds.push(...batchResult.outputIds);
      partialSuccess = true;
    }
  } catch (e) {
    if (partialSuccess) {
      notifications.error(notifications.getFriendlyError(e) ?? 'An unexpected error occured.');
      return {resultType: ActionResultType.PARTIAL_SUCCESS, responseData, submittedRange};
    }
    throw e;
  }

  return {resultType: ActionResultType.SUCCESS, responseData};
}

export async function doPerformCreateOutputs(
  entryId: string,
  structuredTable: ProcessOutputStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  outputSubmitParams: ProcessOutputSubmitParams,
  tableSubmissionDiffs?: TableSubmissionDiffs,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.ProcessOutputSubmitResponse>> {
  return await attemptBatchedCreateOutputs(
    entryId,
    structuredTable,
    computedTable,
    actionFunctionContext,
    outputSubmitParams,
    tableSubmissionDiffs,
    selection,
    updateStatus,
    dataModsHeader
  );
}

async function onInvokeCreateOutputsActionFunction({
  entryOrTemplateId,
  structuredTable,
  structuredTableAdapter,
  computedTable,
  actionFunctionContext,
  table,
}: TableActionFunctionParameters<
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata
>): Promise<void> {
  assert(
    entryOrTemplateId != null && APIUtil.isBaseEntry(entryOrTemplateId),
    'This action is only supported in entries.',
    {apiId: entryOrTemplateId}
  );
  const outputs = outputTableToOutputsForCreateOrUpdate({
    table: structuredTable,
    evaluatedTable: computedTable,
    creationOriginIds: Array(computedTable.length).fill(entryOrTemplateId),
    sourceType: `entry_${getSourceTypeForStructuredTable(structuredTable)}_direct`,
    actionOrigin: ActionOrigin.NOTEBOOK_ENTRY_WORKFLOW_OUTPUT_TABLE,
    actionMethod: ActionMethod.BUTTON_CLICK,
    debugInfo: {entryOrTemplateId},
  });

  await submitOutputsWithConfirmation({
    outputs,
    onRequestSubmit: async (outputSubmitParams: ProcessOutputSubmitParams) => {
      await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.ProcessOutputSubmitResponse>({
        entryId: entryOrTemplateId,
        loadingScreenMessage: 'Submitting Outputs...',
        autoSaverAction: ACTION_SUBMIT_PROCESS_OUTPUTS,
        doPerformAction: (
          tableSubmissionDiffs?: TableSubmissionDiffs,
          updateStatus?: (msg: string) => void,
          dataModsHeader?: DataModificationsHeader
        ) =>
          doPerformCreateOutputs(
            entryOrTemplateId,
            structuredTable,
            computedTable,
            actionFunctionContext,
            outputSubmitParams,
            tableSubmissionDiffs,
            undefined, // selection
            updateStatus,
            dataModsHeader
          ),
        dataModsItemFn: (structuredTables, structuredTableAdapters, computedTables, tableSubmissionUpdates) =>
          getDataModsCountForTableSubmission(structuredTables, tableSubmissionUpdates),
        structuredTables: [structuredTable],
        structuredTableAdapters: [structuredTableAdapter],
        computedTables: [computedTable],
        tables: table ? [table] : [],
      });
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_OUTPUT_TABLE_SUBMIT);
    },
  });
}

export const submitProcessOutputsActionFunction = makeSimpleActionFunction<
  ProcessOutputStructuredTableMetadata,
  {}
>({
  label: (params) => getSubmitButtonLabel(params.tableStatus),
  icon: getSubmitActionFunctionIcon(),
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params.tableStatus, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeCreateOutputsActionFunction,
  getDisabled: isTableActionButtonDisabled,
  getTooltip: tableActionButtonDisabledTooltip,
  getHidden: (params) => getSubmitButtonHidden(params.tableStatus, params.isCollapsed),
});
